import { useCallback, useMemo, useState } from "react";

// Set-based multi-row selection, shared by every table that has checkbox
// bulk-select (Trucks, Amended-return mileage vehicles, VIN corrections,
// Weight-increase worksheet). Selection is keyed by whatever id the caller
// passes to toggle/selectAll: for backend-persisted rows that's the real
// numeric row id; for local-only rows (no backend id yet - see TrucksStep's
// unauthenticated dashboard-demo fallback) callers pass a stable per-row key
// instead (index, or a client-generated id). A single table only ever runs
// in one mode at a time, so the keys are never mixed within one selection set.
export function useRowSelection() {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Pass every selectable id currently loaded (every table this hook backs
  // already loads its full row list client-side, even the ones with their
  // own client-side pagination - see Requirement 15) to select them all;
  // pass an empty array to unselect everything.
  const selectAll = useCallback((ids) => {
    setSelectedIds(new Set(ids));
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  return {
    selectedIds,
    toggle,
    selectAll,
    clear,
    isSelected,
    count: selectedIds.size,
  };
}

// Header checkbox tri-state (unchecked / checked / indeterminate) against a
// given list of selectable ids - the full loaded list, not just a page (see
// selectAll above). Kept outside the hook itself so callers can memoize it
// against their own row list without re-deriving selectedIds first.
export function useHeaderCheckboxState(selectedIds, ids) {
  return useMemo(() => {
    const selectableCount = ids.length;
    if (selectableCount === 0) return { checked: false, indeterminate: false };
    const selectedCount = ids.reduce((n, id) => n + (selectedIds.has(id) ? 1 : 0), 0);
    return {
      checked: selectedCount === selectableCount,
      indeterminate: selectedCount > 0 && selectedCount < selectableCount,
    };
  }, [selectedIds, ids]);
}
